import { collabStateManager } from '@databases/collab-state-manager';
import { getVersionId } from '@databases/database-state';
import { DatabasePermissionsManager } from '@databases/permissions';
import { PluginKey } from '@databases/plugins-types';
import { appStore } from '@databases/redux/store';
import { container, getService } from '@databases/service-container';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';

import { type Recyclable } from './types.ts';

function resolveInitialLockState(
  collabManager: ReturnType<typeof collabStateManager.getCollabManager>,
  database: ReturnType<typeof selectDatabaseByContentIdOptional>,
): boolean {
  if (collabManager) {
    return collabManager.database.isLocked ?? false;
  }

  if (database && !isBrokenDatabase(database)) {
    return database.meta?.isLocked ?? false;
  }

  return false;
}

export function initializePermissionsManager(): Recyclable {
  const unsubscribes: (() => void)[] = [];
  let current: DatabasePermissionsManager | undefined;

  return {
    setup(contentId: string) {
      const database = selectDatabaseByContentIdOptional(appStore.getState(), {
        contentId,
      });
      const initialCollabManager = collabStateManager.getCollabManager(contentId);

      const permissionsManager = new DatabasePermissionsManager({
        canUpdateMetadata: database?.operations.updateMetadata ?? false,
        canUpdateEntries: database?.operations.updateEntries ?? false,
        canDelete: database?.operations.delete ?? false,
        isLocked: resolveInitialLockState(initialCollabManager, database),
        isViewingVersion: !!getVersionId(),
        isEditEnabled: getService('PluginsManager').isPluginEnabled(
          PluginKey.EDIT_CONTENT,
        ),
      });

      current = permissionsManager;
      container.registerSingleton('PermissionsManager', () => permissionsManager);

      // Wire collab → permissions. This runs once per attached collab manager:
      // synchronously now if one is already present (the common standalone
      // case), and again later if one attaches after setup (the embed case).
      // Without the late-attach path, embed bootstraps that race the bridge
      // identity event ahead of the database load can permanently strand the
      // permissions manager at its safe defaults.
      const wireCollabManager = (
        collabManager: NonNullable<typeof initialCollabManager>,
      ) => {
        // Seed from collab's CURRENT state — `onPermissionsChanged` only fires
        // on read-only flips, so without this the initial backend permissions
        // would never reach the manager.
        permissionsManager.setOperations({
          canUpdateMetadata: !collabManager.isReadOnly,
          canUpdateEntries: !collabManager.isReadOnly,
          canDelete: !collabManager.isReadOnly,
        });
        permissionsManager.setLocked(collabManager.database.isLocked ?? false);

        unsubscribes.push(
          collabManager.onPermissionsChanged((isReadOnly) => {
            permissionsManager.setOperations({
              canUpdateMetadata: !isReadOnly,
              canUpdateEntries: !isReadOnly,
              canDelete: !isReadOnly,
            });
          }),
        );

        unsubscribes.push(
          collabManager.database.observeSettings(() => {
            permissionsManager.setLocked(collabManager.database.isLocked ?? false);
          }),
        );
      };

      unsubscribes.push(
        collabStateManager.onCollabManagerAttached(contentId, wireCollabManager),
      );
    },

    dispose() {
      for (const unsub of unsubscribes) {
        unsub();
      }
      unsubscribes.length = 0;

      current?.dispose();
      current = undefined;
    },
  };
}
